import { useRef, useState } from "react";
import api, { downloadBlob } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/ui-helpers";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileArrowUp, ArrowsLeftRight, FileXls, FilePdf, ShieldCheck, X } from "@phosphor-icons/react";

const ACCEPT = ".pdf,.docx,.xlsx,.xlsm,.csv,.txt,.md,.log,.json,.xml,.yaml,.yml,.ini,.cfg,.rpt,.out,.dat";

const FileSlot = ({ label, hint, file, setFile, testid }) => {
  const ref = useRef(null);
  return (
    <div className="border border-border bg-card p-5 flex-1">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
      <input ref={ref} type="file" accept={ACCEPT} className="hidden" data-testid={`${testid}-input`}
             onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); e.target.value = ""; }} />
      {file ? (
        <div className="mt-3 flex items-center justify-between gap-2 border border-border bg-secondary/40 px-3 py-2">
          <span className="text-sm truncate" data-testid={`${testid}-name`}>{file.name}</span>
          <button onClick={() => setFile(null)} className="text-muted-foreground hover:text-foreground" data-testid={`${testid}-clear`}>
            <X size={14} />
          </button>
        </div>
      ) : (
        <Button variant="outline" className="mt-3 rounded-none w-full" onClick={() => ref.current?.click()} data-testid={`${testid}-btn`}>
          <FileArrowUp size={16} className="mr-1.5" /> Choose file
        </Button>
      )}
    </div>
  );
};

const StatChip = ({ label, value, cls }) => (
  <div className={`border px-4 py-2 ${cls}`} data-testid={`dc-stat-${label.toLowerCase()}`}>
    <p className="text-lg font-bold leading-none">{value}</p>
    <p className="text-[10px] uppercase tracking-wider mt-1">{label}</p>
  </div>
);

export default function DocChecker() {
  const [fileA, setFileA] = useState(null);
  const [fileB, setFileB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [needsKey, setNeedsKey] = useState(null);
  const [keyColumn, setKeyColumn] = useState("");
  const [result, setResult] = useState(null);

  const compare = async (key) => {
    if (!fileA || !fileB) { toast.error("Choose both documents first."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file_a", fileA);
      fd.append("file_b", fileB);
      if (key) fd.append("key_column", key);
      const { data } = await api.post("/doc-checker/compare", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.needs_key) {
        setNeedsKey(data);
        setResult(null);
        return;
      }
      setResult(data);
      setNeedsKey(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Comparison failed");
    } finally { setBusy(false); }
  };

  const reset = () => { setFileA(null); setFileB(null); setResult(null); setNeedsKey(null); setKeyColumn(""); };

  const dl = async (fmt) => {
    try { await downloadBlob(`/doc-checker/export/${result.comparison_id}?fmt=${fmt}`, `delta-report.${fmt}`); }
    catch { toast.error("Export failed — the comparison may have expired. Re-run it."); }
  };

  const s = result?.stats;

  return (
    <div className="p-6 space-y-5 max-w-6xl" data-testid="doc-checker-page">
      <PageHeader title="Document Checker"
                  subtitle="Compare two documents and get the exact deltas — Tripwire reports, personnel lists, procedures, modeling data." />
      <div className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-2 w-fit" data-testid="dc-no-ai-note">
        <ShieldCheck size={14} weight="fill" />
        100% in-app comparison — your documents are never sent to AI or any external service, and nothing is stored.
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <FileSlot label="Document A — baseline / original" hint="PDF, Word, Excel, CSV or text" file={fileA} setFile={setFileA} testid="dc-file-a" />
        <FileSlot label="Document B — revised / current" hint="PDF, Word, Excel, CSV or text" file={fileB} setFile={setFileB} testid="dc-file-b" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={() => compare(keyColumn || null)} disabled={busy || !fileA || !fileB}
                className="rounded-none bg-primary hover:bg-primary/90" data-testid="dc-compare-btn">
          <ArrowsLeftRight size={16} className="mr-1.5" /> {busy ? "Comparing…" : "Compare documents"}
        </Button>
        {(result || needsKey) && (
          <Button variant="outline" className="rounded-none" onClick={reset} data-testid="dc-reset-btn">Start over</Button>
        )}
      </div>

      {needsKey && (
        <div className="border border-border bg-card p-5 max-w-xl" data-testid="dc-key-picker">
          <p className="text-sm font-semibold">Pick the key column</p>
          <p className="text-xs text-muted-foreground mt-1">
            Both spreadsheets detected ({needsKey.row_counts.a} vs {needsKey.row_counts.b} rows). Choose the column that
            uniquely identifies each row (e.g., employee ID, hostname, asset tag) so rows can be matched.
          </p>
          <div className="flex items-center gap-3 mt-3">
            <Select value={keyColumn} onValueChange={setKeyColumn}>
              <SelectTrigger className="rounded-none w-64" data-testid="dc-key-select"><SelectValue placeholder="Select key column…" /></SelectTrigger>
              <SelectContent>
                {needsKey.columns.map((c) => <SelectItem key={c} value={c} data-testid={`dc-key-opt-${c}`}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button disabled={!keyColumn || busy} onClick={() => compare(keyColumn)} className="rounded-none" data-testid="dc-key-run-btn">
              Run comparison
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-4" data-testid="dc-results">
          {result.note && (
            <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 px-3 py-2" data-testid="dc-mode-note">{result.note}</p>
          )}
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div className="flex gap-3 flex-wrap">
              <StatChip label="Added" value={s.added} cls="border-blue-300 bg-blue-50 text-blue-800" />
              <StatChip label="Removed" value={s.removed} cls="border-orange-300 bg-orange-50 text-orange-800" />
              <StatChip label="Changed" value={s.changed} cls="border-slate-300 bg-slate-100 text-slate-700" />
              <StatChip label="Unchanged" value={s.unchanged} cls="border-border bg-secondary/40 text-muted-foreground" />
              {s.similarity !== undefined && (
                <StatChip label="Similarity" value={`${s.similarity}%`} cls="border-border bg-secondary/40 text-muted-foreground" />
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-none" onClick={() => dl("xlsx")} data-testid="dc-export-xlsx">
                <FileXls size={16} className="mr-1.5" /> Excel
              </Button>
              <Button variant="outline" className="rounded-none" onClick={() => dl("pdf")} data-testid="dc-export-pdf">
                <FilePdf size={16} className="mr-1.5" /> PDF
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            <span className="font-mono">{result.file_a}</span> → <span className="font-mono">{result.file_b}</span>
            {result.mode === "table" && <> · keyed on <span className="font-semibold">{result.key_column}</span></>}
            &nbsp;· result available for download for {result.expires_in_minutes} minutes, then discarded.
          </p>
          {result.mode === "table" ? <TableDelta result={result} /> : <TextDelta result={result} />}
        </div>
      )}
    </div>
  );
}

const TableDelta = ({ result }) => {
  const cols = result.columns.slice(0, 10);
  const section = (title, rows, cls, testid) => rows.length > 0 && (
    <div className="border border-border bg-card" data-testid={testid}>
      <p className={`text-xs font-semibold uppercase tracking-wider px-3 py-2 ${cls}`}>{title} ({rows.length})</p>
      <div className="overflow-x-auto max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-secondary sticky top-0">
            <tr>{cols.map((c) => <th key={c} className="text-left px-2 py-1.5 whitespace-nowrap">{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border">
                {cols.map((c) => <td key={c} className="px-2 py-1 whitespace-nowrap max-w-[16rem] truncate" title={r[c]}>{r[c]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
  return (
    <div className="space-y-4">
      {(result.duplicate_keys || []).length > 0 && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 px-3 py-2">
          Duplicate key values detected (last occurrence used): {result.duplicate_keys.join(", ")}
        </p>
      )}
      {section("Added rows", result.added, "text-blue-800 bg-blue-50", "dc-added-rows")}
      {section("Removed rows", result.removed, "text-orange-800 bg-orange-50", "dc-removed-rows")}
      {result.changed.length > 0 && (
        <div className="border border-border bg-card" data-testid="dc-changed-rows">
          <p className="text-xs font-semibold uppercase tracking-wider px-3 py-2 text-slate-700 bg-slate-100">Changed rows ({result.changed.length})</p>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-secondary sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5">{result.key_column}</th>
                  <th className="text-left px-2 py-1.5">Column</th>
                  <th className="text-left px-2 py-1.5">Old value</th>
                  <th className="text-left px-2 py-1.5">New value</th>
                </tr>
              </thead>
              <tbody>
                {result.changed.map((item) => item.changes.map((ch, i) => (
                  <tr key={`${item.key}-${ch.column}`} className="border-t border-border">
                    <td className="px-2 py-1 font-medium">{i === 0 ? item.key : ""}</td>
                    <td className="px-2 py-1">{ch.column}</td>
                    <td className="px-2 py-1 text-orange-800 line-through max-w-[18rem] truncate" title={ch.old}>{ch.old || "—"}</td>
                    <td className="px-2 py-1 text-blue-800 max-w-[18rem] truncate" title={ch.new}>{ch.new || "—"}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {result.added.length === 0 && result.removed.length === 0 && result.changed.length === 0 && (
        <p className="text-sm text-muted-foreground border border-border bg-card p-4" data-testid="dc-identical">
          No differences — every keyed row matches.
        </p>
      )}
    </div>
  );
};

// Merge a_seg/b_seg into one inline delta: gray context, old struck-through red, new green.
const MergedDelta = ({ a_seg, b_seg }) => {
  const out = [];
  let i = 0, j = 0, k = 0;
  while (i < a_seg.length || j < b_seg.length) {
    if (i < a_seg.length && a_seg[i][1]) {
      out.push(<del key={k++} className="bg-orange-200 text-orange-900 no-underline line-through px-0.5">{a_seg[i][0]}</del>);
      i += 1;
    } else if (j < b_seg.length && b_seg[j][1]) {
      out.push(<ins key={k++} className="bg-blue-200 text-blue-900 no-underline px-0.5">{b_seg[j][0]}</ins>);
      j += 1;
    } else {
      out.push(<span key={k++} className="text-muted-foreground">{i < a_seg.length ? a_seg[i][0] : b_seg[j]?.[0]}</span>);
      i += 1;
      j += 1;
    }
  }
  return out;
};

const TYPE_BADGE = {
  add: ["Added", "text-blue-800 bg-blue-50 border-blue-300"],
  del: ["Removed", "text-orange-800 bg-orange-50 border-orange-300"],
  chg: ["Changed", "text-slate-700 bg-slate-100 border-slate-300"],
};

const TextDelta = ({ result }) => {
  const deltas = result.rows.filter((r) => r.t === "add" || r.t === "del" || r.t === "chg");
  if (deltas.length === 0) {
    return (
      <p className="text-sm text-muted-foreground border border-border bg-card p-4" data-testid="dc-identical">
        The documents are identical.
      </p>
    );
  }
  return (
    <div className="border border-border bg-card" data-testid="dc-text-diff">
      {result.truncated && (
        <p className="text-xs text-amber-800 bg-amber-50 border-b border-amber-300 px-3 py-2">
          Large delta — showing the first rows. The Excel export contains the full delta.
        </p>
      )}
      <div className="overflow-x-auto max-h-[36rem] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-secondary sticky top-0">
            <tr>
              <th className="text-left px-2 py-1.5 w-20">Type</th>
              <th className="text-right px-2 py-1.5 w-12">A#</th>
              <th className="text-right px-2 py-1.5 w-12">B#</th>
              <th className="text-left px-2 py-1.5">Delta</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {deltas.map((r, i) => {
              const [label, cls] = TYPE_BADGE[r.t];
              return (
                <tr key={i} className="border-t border-border align-top">
                  <td className="px-2 py-1">
                    <span className={`text-[10px] uppercase tracking-wider border px-1.5 py-0.5 font-sans ${cls}`}>{label}</span>
                  </td>
                  <td className="px-2 py-1 text-right text-muted-foreground select-none">{r.an || ""}</td>
                  <td className="px-2 py-1 text-right text-muted-foreground select-none">{r.bn || ""}</td>
                  <td className="px-2 py-1 whitespace-pre-wrap break-all">
                    {r.t === "add" && <span className="text-blue-800">{r.b}</span>}
                    {r.t === "del" && <span className="text-orange-800 line-through">{r.a}</span>}
                    {r.t === "chg" && (r.a_seg ? (
                      <MergedDelta a_seg={r.a_seg} b_seg={r.b_seg || []} />
                    ) : (
                      <>
                        <div className="text-orange-800 line-through">{r.a}</div>
                        <div className="text-blue-800">{r.b}</div>
                      </>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
